import { ReportStatus } from '@app-model/vehicle';

export const EXCHANGE_FILTER_DIFFERENTIAL_LABELS = {
  withDossier: 'Com RaioX Auto Arremate',
  withReport: 'Com laudo',
  extraKey: 'Com chave reserva',
  carManual: 'Com manual do proprietário',
} as const;

export const EXCHANGE_FILTER_REPORT_STATUS_KEYS = [
  'APPROVED',
  'WITH_RESTRICTIONS',
  'DISAPPROVED',
] as const;

export type ExchangeFilterReportStatusKey =
  (typeof EXCHANGE_FILTER_REPORT_STATUS_KEYS)[number];

export function formatReportStatusLabel(status: string): string {
  const reportLabel = ReportStatus[status as keyof typeof ReportStatus] ?? status;
  return `Laudo ${reportLabel.toLowerCase()}`;
}

export type ActiveFilterChipKey =
  | { type: 'search' }
  | { type: 'segment'; value: string }
  | { type: 'dealType'; value: string }
  | { type: 'make'; value: string }
  | { type: 'model'; value: string }
  | { type: 'city'; value: string }
  | { type: 'uf'; value: string }
  | { type: 'fuel'; value: string }
  | { type: 'transmission'; value: string }
  | { type: 'modelYear' }
  | { type: 'price' }
  | { type: 'km' }
  | { type: 'fipe' }
  | { type: 'oemDealer'; value: string }
  | { type: 'withDossier' }
  | { type: 'withReport' }
  | { type: 'reportStatus'; value: string }
  | { type: 'extraKey' }
  | { type: 'carManual' }
  | { type: 'equipment'; value: string }
  | { type: 'publishedPeriod' }
  | { type: 'onlyWithFinalBid' }
  | { type: 'almostNew' }
  | { type: 'used' };

export const EXCHANGE_FILTER_DEAL_TYPE_KEYS = ['DEFAULT', 'FAST_QUOTE', 'SHOWROOM'] as const;

export type ExchangeFilterDealTypeKey = (typeof EXCHANGE_FILTER_DEAL_TYPE_KEYS)[number];

export interface ExchangeFilterPageScope {
  statuses?: string[];
  dealTypes?: string[];
  onlyFavorites?: boolean;
  onlyWithMyBids?: boolean;
  boughtByMe?: boolean;
  myVehicles?: boolean;
}

export function isDefaultDealTypeSelection(
  dealTypes: readonly string[] | null | undefined,
): boolean {
  if (!dealTypes || dealTypes.length !== EXCHANGE_FILTER_DEAL_TYPE_KEYS.length) {
    return false;
  }

  const selected = new Set(dealTypes);
  return EXCHANGE_FILTER_DEAL_TYPE_KEYS.every((key) => selected.has(key));
}

export type ExchangeFilterPage = 'CAST_OFF' | 'FAST_QUOTE' | 'ALL';
export const EXCHANGE_FILTER_BUY_NOW_SUPPORTED = false;
export type PublishedPeriodKey = 'today' | 'last3' | 'lastWeek' | 'lastMonth';

export const PUBLISHED_PERIOD_OPTIONS: ReadonlyArray<{
  value: PublishedPeriodKey;
  label: string;
}> = [
  { value: 'today', label: 'Hoje' },
  { value: 'last3', label: 'Últimos 3 dias' },
  { value: 'lastWeek', label: 'Última semana' },
  { value: 'lastMonth', label: 'Último mês' },
];

export interface ActiveFilterChip {
  id: string;
  label: string;
  key: ActiveFilterChipKey;
}

export interface ActiveFilterLabelContext {
  makeNames?: Record<string, string>;
  modelNames?: Record<string, string>;
  equipmentNames?: Record<string, string>;
  oemDealerNames?: Record<string, string>;
}

export interface FilterDetailItem {
  id: string;
  name: string;
}

export interface FilterDetailBuildOptions {
  hideOemDealerType?: boolean;
  legacyCityLabel?: string;
}

export type FipeRangeTone = 'below' | 'above' | 'mixed';

export interface FipeRangeValue {
  min: number;
  max: number;
}

export function resolveFipeRangeTone(value: FipeRangeValue): FipeRangeTone {
  if (value.min > 0) {
    return 'above';
  }

  if (value.max < 0) {
    return 'below';
  }

  return 'mixed';
}

export function applyPublishedPeriodToFilter(
  period: PublishedPeriodKey | null,
): string | undefined {
  if (!period) {
    return undefined;
  }

  const now = new Date();

  if (period === 'today') {
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    return startOfToday.toISOString();
  }

  const daysByPeriod: Record<Exclude<PublishedPeriodKey, 'today'>, number> = {
    last3: 3,
    lastWeek: 7,
    lastMonth: 30,
  };

  const days = daysByPeriod[period];
  const publishedSince = new Date(now);
  publishedSince.setDate(publishedSince.getDate() - days);
  return publishedSince.toISOString();
}

export function resolvePublishedPeriodKey(
  firstPublishedAtIni: string | undefined,
): PublishedPeriodKey | null {
  if (!firstPublishedAtIni) {
    return null;
  }

  const publishedSince = new Date(firstPublishedAtIni);
  if (Number.isNaN(publishedSince.getTime())) {
    return null;
  }

  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  if (publishedSince >= startOfToday) {
    return 'today';
  }

  const diffInDays = Math.floor(
    (now.getTime() - publishedSince.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays <= 3) {
    return 'last3';
  }

  if (diffInDays <= 7) {
    return 'lastWeek';
  }

  if (diffInDays <= 30) {
    return 'lastMonth';
  }

  return null;
}

export function getPublishedPeriodLabel(
  period: PublishedPeriodKey,
): string {
  return PUBLISHED_PERIOD_OPTIONS.find((option) => option.value === period)?.label ?? period;
}
